import { getLocalApi } from "./localApi";
import apiConfig from "app/api/apiConfig.json";
import { ApiErrors } from "./ApiErrors";

export type SaveCentreBody = {
  name: string;
  countryCode: string;
  currencyCode: string;
  centreCode: string;
  isActive: boolean;
  physicalCentre: boolean;
  printFormat: number;
  brandColorHex?: string;
  generalEmail?: string;
  accommodationEmail?: string;
  telephone?: string;
  emergencyTelephone?: string;
  schoolSponsorshipNumber?: string;
  vatNumber?: string;
  registrationNumber?: string;
  vatExemptionNumber?: string;
  chequePayableTo?: string;
  address?: {
    id?: number;
    line1?: string; line2?: string; line3?: string; line4?: string;
    countryISOCode?: string;
  };
  logo?: {
    image?: string;
    contentType?: string;
    fileName?: string;
  };
  contacts?: Array<{
    id?: number;
    contactType: number;
    name?: string;
    email?: string;
    signatureImage?: string;
    signatureContentType?: string;
    signatureFileName?: string;
  }>;
  bankDetails?: {
    id?: number;
    beneficiaryName?: string;
    accountNumber?: string;
    bankName?: string;
    iban?: string;
    swiftCode?: string;
    branchCode?: string;
    abaRoutingNo?: string;
    achAba?: string;
    intermediaryBankName?: string;
    intermediarySwiftCode?: string;
    bankAddress?: {
      id?: number; line1?: string; line2?: string; line3?: string; line4?: string; countryISOCode?: string;
    };
    beneficiaryBankAddress?: {
      id?: number; line1?: string; line2?: string; line3?: string; line4?: string; countryISOCode?: string;
    };
    intermediaryBankAddress?: {
      id?: number; line1?: string; line2?: string; line3?: string; line4?: string; countryISOCode?: string;
    };
  };
};

export interface SaveCentreParams extends SaveCentreBody {
  id?: number;
  centreId?: number;
}

function endpointPath(
  key: string,
  fallback: string,
  params?: Record<string, string>
): string {
  const tmpl = ((apiConfig as any)?.[key] as string) || fallback;
  if (!params) return tmpl;
  return Object.keys(params).reduce(
    (acc, k) => acc.replace(new RegExp(`{${k}}`, "g"), params[k]),
    tmpl
  );
}

function requireString(val: any, field: string): string {
  const s = String(val ?? "").trim();
  if (!s) throw new Error(`${field} is required`);
  return s;
}

function buildBody(input: SaveCentreParams): SaveCentreBody {
  const { id, centreId, ...rest } = input;

  requireString(rest.name, "name");
  requireString(rest.countryCode, "countryCode");
  requireString(rest.currencyCode, "currencyCode");
  requireString(rest.centreCode, "centreCode");

  const isActive =
    typeof rest.isActive === "boolean" ? rest.isActive : true;
  const physicalCentre =
    typeof rest.physicalCentre === "boolean" ? rest.physicalCentre : false;

  const printFormat =
    typeof rest.printFormat === "number" && !Number.isNaN(rest.printFormat)
      ? rest.printFormat
      : 0;

  return {
    ...rest,
    isActive,
    physicalCentre,
    printFormat,
  };
}

export async function saveCentre(
  params: SaveCentreParams
): Promise<{ ok: boolean; message: string; errors?: string[]; data?: any }> {
  const api = await getLocalApi();
  const isUpdate = !!(params.id ?? params.centreId);

  const body = buildBody(params);

  const idStr =
    params.id != null
      ? String(params.id)
      : params.centreId != null
      ? String(params.centreId)
      : "";

  const path = isUpdate
    ? endpointPath("put_centre", "Centres/{id}", {
        id: encodeURIComponent(idStr),
      })
    : endpointPath("post_centre", "Centres");

  const method = isUpdate ? "PUT" : "POST";

  const res = await api.call(path, method, JSON.stringify(body));
  if (!res) return { ok: false, message: "No response from API", data: null };

  const text = await res.text().catch(() => "");
  const data = text ? safeJsonParse(text) : null;

  const errors = ApiErrors.extractAllErrors(data, res, "Failed to save centre");
  const message = res.ok
    ? (errors.length === 0 ? ((data as any)?.message || (isUpdate ? "Centre updated" : "Centre created")) : ApiErrors.formatErrors(errors))
    : ApiErrors.formatErrors(errors);

  return { ok: res.ok, message, errors: res.ok && errors.length === 0 ? undefined : errors, data };
}

function safeJsonParse(s: string) {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}
